// import { getObjectFromIndex, saveObjectToIndex, deleteObjectFromIndex } from 'src/utils/indexedDb';

const SECRET_KEY =
  '714bcfb089dd121b789f41cb0f78fbfa5fa14f60c26ed1abcc9b13519a48c3dc'; // Must be exactly 32 chars for AES-256
// const TOKEN = '9f86d081884c7d659a2feaa0c55ad015'; // To validate stored data

// 📌 Convert ArrayBuffer to Base64 safely
function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// 📌 Convert Base64 to ArrayBuffer
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

// Convert HEX string to Uint8Array
function hexToUint8Array(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}

// 📌 Generate a Crypto Key
async function generateKey(): Promise<CryptoKey> {
  const keyData = hexToUint8Array(SECRET_KEY); // Convert HEX to bytes
  return crypto.subtle.importKey(
    'raw',
    keyData as BufferSource,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
}

// // 📌 Encrypt & Store Data Securely
// export async function storeSecureData(key: string, data: any) {
//     try {

//         const jsonString = JSON.stringify(data);
//         const encodedData = new TextEncoder().encode(jsonString);

//         const cryptoKey = await generateKey();
//         const iv = crypto.getRandomValues(new Uint8Array(12)); // Generate IV for AES-GCM
//         const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, encodedData);

//         const storedValue = JSON.stringify({
//             iv: arrayBufferToBase64(iv),
//             encryptedData: arrayBufferToBase64(encryptedData),
//             token: TOKEN,
//         });

//         await saveObjectToIndex(key, storedValue);
//         console.log('Data securely stored.');
//     } catch (error) {
//         console.error('Error storing data securely:', error);
//     }
// }

// // 📌 Retrieve & Decrypt Data Securely
// export async function getSecureData(key: string): Promise<any> {
//     try {
//         const storedData = await getObjectFromIndex(key);
//         if (!storedData) return null;

//         const { iv, encryptedData, token } = JSON.parse(storedData);
//         if (token !== TOKEN) throw new Error('Invalid security token!');

//         const cryptoKey = await generateKey();
//         const decryptedData = await crypto.subtle.decrypt(
//             { name: 'AES-GCM', iv: base64ToArrayBuffer(iv) },
//             cryptoKey,
//             base64ToArrayBuffer(encryptedData)
//         );

//         return JSON.parse(new TextDecoder().decode(decryptedData)) as Promise<any>;
//     } catch (error) {
//         console.error('Error retrieving secure data:', error);
//         return null;
//     }
// }

// 📌 Remove Data from Storage
// export async function removeSecureData(key: string) {
//     await deleteObjectFromIndex(key);
//     console.log('Data removed.');
// }

// 📌 Encrypt Data
export async function encodeData(data: any) {
  try {
    const jsonString = JSON.stringify(data);
    const encodedData = new TextEncoder().encode(jsonString);

    const cryptoKey = await generateKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      cryptoKey,
      encodedData,
    );

    const encryptedArray = new Uint8Array(encryptedBuffer);
    const tagLengthBytes = 16;

    const ciphertext = encryptedArray.slice(
      0,
      encryptedArray.length - tagLengthBytes,
    );
    const tag = encryptedArray.slice(encryptedArray.length - tagLengthBytes);

    const storedValue = JSON.stringify({
      iv: arrayBufferToBase64(iv),
      encryptedData: arrayBufferToBase64(ciphertext.buffer),
      tag: arrayBufferToBase64(tag.buffer),
    });

    return storedValue;
  } catch (error) {
    console.error('Error Encrypt data:', error);
  }
}

// 📌 Decrypt Data
export async function decodeData(encryptedPayload: string): Promise<any> {
  try {
    const { iv, encryptedData, tag } = JSON.parse(encryptedPayload);

    const ivArrayBuffer = base64ToArrayBuffer(iv);
    const encryptedDataArrayBuffer = base64ToArrayBuffer(encryptedData);
    const tagArrayBuffer = base64ToArrayBuffer(tag);

    const ciphertext = new Uint8Array(encryptedDataArrayBuffer);
    const authenticationTag = new Uint8Array(tagArrayBuffer);
    const combinedData = new Uint8Array(
      ciphertext.length + authenticationTag.length,
    );
    combinedData.set(ciphertext);
    combinedData.set(authenticationTag, ciphertext.length);

    const cryptoKey = await generateKey();

    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivArrayBuffer, tagLength: 128 },
      cryptoKey,
      combinedData.buffer,
    );

    const decryptedText = new TextDecoder().decode(decryptedBuffer);
    return JSON.parse(decryptedText) as string;
  } catch (error) {
    console.error('Error decrypting data:', error);
    return null;
  }
}
